import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { productService } from '../services/productService';
import { Category } from '../types';
import './Home.css';

const Home: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await productService.getCategories();
        setCategories(data.slice(0, 6)); // Show only first 6 categories on home page
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    };

    loadCategories();
  }, []);

  return (
    <div className="home-container">
      <section className="hero">
        <div className="hero-content">
          <h1>Welcome to 21</h1>
          <p className="hero-subtitle">{t('home.hero.title')}</p>
          <p className="hero-description">
            {t('home.hero.subtitle')}
          </p>
          <div className="hero-actions">
            <Link to="/products" className="btn-hero-primary">
              {t('home.hero.cta')}
            </Link>
            {!user && (
              <Link to="/login" className="btn-hero-secondary">
                {t('nav.login')}
              </Link>
            )}
          </div>
        </div>
        <div className="hero-image">
          <img
            src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=600"
            alt="Fresh groceries"
            loading="lazy"
          />
        </div>
      </section>

      <section className="features">
        <div className="feature-card">
          <div className="feature-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <h3>{t('home.features.fast.title')}</h3>
          <p>{t('home.features.fast.description')}</p>
        </div>

        <div className="feature-card">
          <div className="feature-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <h3>{t('home.features.fresh.title')}</h3>
          <p>{t('home.features.fresh.description')}</p>
        </div>

        <div className="feature-card">
          <div className="feature-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
          </div>
          <h3>{t('home.features.quality.title')}</h3>
          <p>{t('home.features.quality.description')}</p>
        </div>
      </section>

      {/* Categories Section */}
      {categories.length > 0 && (
        <section className="categories-section">
          <h2>{t('home.categories.title')}</h2>
          <div className="categories-grid">
            {categories.map((category) => (
              <Link
                key={category.id}
                to="/products"
                className="category-card"
              >
                <img
                  src={category.pictureUrl}
                  alt={category.name}
                  loading="lazy"
                />
                <h3>{category.name}</h3>
              </Link>
            ))}
          </div>
          <Link to="/products" className="btn-view-all">
            {t('home.categories.viewAll')}
          </Link>
        </section>
      )}

      <section className="cta">
        <h2>{t('home.hero.cta')}</h2>
        <p>{t('home.hero.subtitle')}</p>
        <Link to="/products" className="btn-cta">
          {t('home.hero.cta')}
        </Link>
      </section>
    </div>
  );
};

export default Home;
